/**
 * @render react
 * @name Subscription
*/

import React from 'react';
import PropTypes from 'prop-types';
import classNames from 'classnames';
import { withPolaris } from 'Components/protons/Polaris';

import { Image } from 'Components/atoms/Image';
import { Heading } from 'Components/molecules/Heading';
import { Link } from 'Components/molecules/Link';

import './_style.scss';

/**
 * Subscription component
 *
 * @param {Object} context - Extra information coming from Polaris provider
 * @param {string} ctaLabel - CTA label
 * @param {node} ctaLabelComponent - CTA label Component
 * @param {string} ctaUrl - CTA url
 * @param {string} excerpt - excerpt to be displayed below the title
 * @param {Object} extraClassNames - Extra classes to be added to the component
 * @param {string} image - object containing image src and alt text
 * @param {String} imageSize - size to be used for the image. Defaults to sponsor
 * @param {boolean} isHeading - component must use <Heading> element
 * @param {string} kicker - Kicker text
 * @param {boolean} loadStyles - Whether to apply the class names for styling
 * @param {string} mainTitle  - main title for the component
 * @param {number} mainTitleSize  - size of the main title heading
 * @param {object} link - props to be passed to Link Component
 * @param {string} secondaryCtaLabel - secondary CTA label
 * @param {string} secondaryCtaUrl - secondary CTA url
 * @param {string} title - title for the component
 * @param {number} titleSize - size of the title heading
 * @param {boolean} wrapLink - Wrap Subscription component by Link
 */
const Subscription = ({
  ctaLabel,
  ctaLabelComponent,
  ctaUrl,
  context: { cssPrefix },
  excerpt,
  extraClassNames,
  image,
  imageSize,
  isHeading,
  kicker,
  link,
  loadStyles,
  mainTitle,
  mainTitleSize,
  secondaryCtaLabel,
  secondaryCtaUrl,
  title,
  titleSize,
  wrapLink,
}) => {
  const titleElement = title && (
    isHeading
      ? (
        <Heading
          size={titleSize}
          extraClassNames={{ [`${cssPrefix}__subscription--title`]: true }}
        >
          {title}
        </Heading>
      )
      : (
        <span className={classNames({
          [`${cssPrefix}__heading`]: true,
          [`${cssPrefix}__subscription--title`]: true,
        })}
        >
          {title}
        </span>
      )
  );

  return (
    <div
      className={classNames({
        [`${cssPrefix}__subscription`]: loadStyles,
        ...extraClassNames,
      })}
    >
      {wrapLink && (
        <Link
          extraClassNames={{ [`${cssPrefix}__subscription--link`]: true }}
          href={ctaUrl}
          screenReaderText={link.screenReaderText || undefined}
        >
          {ctaLabelComponent || ctaLabel}
        </Link>
      )}

      {mainTitle && (
      <Heading
        size={mainTitleSize}
        extraClassNames={{ [`${cssPrefix}__subscription--main-heading`]: true }}
      >
        <span>{mainTitle}</span>
      </Heading>
      )}
      <div className={`${cssPrefix}__subscription--container`}>
        {image && image.src && (
        <div className={`${cssPrefix}__subscription--image`}>
          <Image {...image} size={imageSize || undefined} />
        </div>
        )}
        <div className={`${cssPrefix}__subscription--content`}>
          {kicker && (
          <span className={`${cssPrefix}__subscription--kicker`}>
            {kicker}
          </span>
          )}

          {titleElement}

          {excerpt && (
          <div className={`${cssPrefix}__subscription--excerpt`}>{excerpt}</div>
          )}

          <div className={`${cssPrefix}__subscription--cta`}>
            <Link
              href={ctaUrl}
              screenReaderText={link.screenReaderText || ctaLabel}
              title={ctaLabel}
            >
              {ctaLabelComponent || ctaLabel}
            </Link>
          </div>
          {secondaryCtaLabel && secondaryCtaUrl && (
            <div className={`${cssPrefix}__subscription--cta-secondary`}>
              <Link
                href={secondaryCtaUrl}
                screenReaderText={secondaryCtaLabel}
                title={secondaryCtaLabel}
              >
                {secondaryCtaLabel}
              </Link>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

Subscription.propTypes = {
  ctaLabel: PropTypes.string,
  ctaLabelComponent: PropTypes.node,
  ctaUrl: PropTypes.string,
  context: PropTypes.shape({
    cssPrefix: PropTypes.string,
  }),
  excerpt: PropTypes.string,
  extraClassNames: PropTypes.shape(),
  image: PropTypes.shape({
    alt: PropTypes.string,
    height: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
    src: PropTypes.string,
    width: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
  }),
  imageSize: PropTypes.string,
  isHeading: PropTypes.bool,
  kicker: PropTypes.string,
  link: PropTypes.shape(),
  loadStyles: PropTypes.bool,
  mainTitle: PropTypes.string,
  mainTitleSize: PropTypes.number,
  secondaryCtaLabel: PropTypes.string,
  secondaryCtaUrl: PropTypes.string,
  title: PropTypes.string,
  titleSize: PropTypes.number,
  wrapLink: PropTypes.bool,
};

Subscription.defaultProps = {
  context: {},
  ctaLabel: '',
  ctaLabelComponent: null,
  ctaUrl: '',
  excerpt: '',
  extraClassNames: {},
  image: {},
  imageSize: 'subscription',
  isHeading: true,
  kicker: '',
  link: {},
  loadStyles: true,
  mainTitle: '',
  mainTitleSize: 4,
  secondaryCtaLabel: null,
  secondaryCtaUrl: null,
  title: '',
  titleSize: 3,
  wrapLink: false,
};

export default withPolaris(Subscription);
